import { createTheme } from "@mui/material/styles";
import { colorsSemantic } from "./tokens/colors-semantic.js";
import { colorsFoundation } from "./tokens/colors-foundation.js";
import { typographyTokens } from "./tokens/typography.js";
import {
  dimensions,
  borderRadius,
  borderWidth,
  componentHeights,
} from "./tokens/dimensions.js";
import components from "./tokens/components.js";
import { hexToRgba } from "./utils/colors.js";

// Soft focus ring in the brand colour, matching the product design's
// `0 0 0 3px rgba(primary, .10)` treatment. Derived from the primary scale so
// it recolours automatically per skin.
const focusRing = `0 0 0 3px ${hexToRgba(colorsFoundation.key.blue[700], 0.1)}`;

/**
 * MUI Theme v2 - Token-based theme configuration
 * Maps design tokens to MUI theme structure
 */
export const muiThemeV2 = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      xxl: 1920,
    },
  },
  // Color palette mapping
  palette: {
    // Primary = Blue (primary brand color)
    primary: {
      main: colorsFoundation.key.blue[700],
      light: colorsFoundation.key.blue[600],
      dark: colorsFoundation.key.blue[800],
      contrastText: colorsSemantic.text.inverse,
    },
    // Secondary = Gold (secondary brand color)
    secondary: {
      main: colorsFoundation.key.gold[600],
      light: colorsFoundation.key.gold[500],
      dark: colorsFoundation.key.gold[700],
      contrastText: colorsSemantic.text.inverse,
    },
    // Text colors
    text: {
      primary: colorsSemantic.text.default,
      secondary: colorsSemantic.text.subtle,
      disabled: colorsSemantic.text.disabled,
    },
    // Background colors - use semantic tokens
    background: colorsSemantic.background,
    // Grey palette for component states
    grey: {
      50: colorsFoundation.neutral[50],
      100: colorsFoundation.neutral[100],
      200: colorsFoundation.neutral[200],
      300: colorsFoundation.neutral[300],
    },
    // Divider color
    divider: colorsSemantic.border.default,
    // Status colors
    error: {
      main: colorsFoundation.status.negative[500],
      light: colorsFoundation.status.negative[400],
      dark: colorsFoundation.status.negative[600],
    },
    warning: {
      main: colorsFoundation.status.critical[500],
    },
    info: {
      main: colorsFoundation.status.informative[500],
    },
    success: {
      main: colorsFoundation.status.positive[500],
    },

    actionCancel: {
      main: "#6E0019",
      contrastText: "#FFFFFF",
    },
  },

  // Typography
  typography: {
    fontFamily: typographyTokens.family.body,
    fontSize: 16,

    h1: {
      fontFamily: typographyTokens.family.heading,
      fontSize: typographyTokens.size["3xl"],
      fontWeight: typographyTokens.weight.semibold,
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: typographyTokens.family.heading,
      fontSize: typographyTokens.size["2xl"],
      fontWeight: typographyTokens.weight.semibold,
      lineHeight: 1.3,
    },
    h3: {
      fontFamily: typographyTokens.family.heading,
      fontSize: typographyTokens.size["xl"],
      fontWeight: typographyTokens.weight.semibold,
      lineHeight: 1.4,
    },
    h4: {
      fontFamily: typographyTokens.family.heading,
      fontSize: typographyTokens.size["lg"],
      fontWeight: typographyTokens.weight.semibold,
      lineHeight: 1.4,
    },
    h5: {
      fontFamily: typographyTokens.family.heading,
      fontSize: typographyTokens.size["lg"],
      fontWeight: typographyTokens.weight.semibold,
      lineHeight: 1.5,
    },
    h6: {
      fontFamily: typographyTokens.family.heading,
      fontSize: typographyTokens.size["base"],
      fontWeight: typographyTokens.weight.semibold,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: typographyTokens.size.base,
      fontWeight: typographyTokens.weight.regular,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: typographyTokens.size.sm,
      fontWeight: typographyTokens.weight.regular,
      lineHeight: 1.5,
    },
    button: {
      fontFamily: typographyTokens.family.body,
      fontSize: typographyTokens.size.base,
      fontWeight: typographyTokens.weight.semibold,
      lineHeight: 1.5,
      textTransform: "none",
    },
    // Canonical page/screen title (matches the click-dummy: 16px / 700).
    // Every page header routes its title through this variant via <PageHeader>.
    pageTitle: {
      fontFamily: typographyTokens.family.heading,
      fontSize: typographyTokens.size.base, // 16px
      fontWeight: typographyTokens.weight.bold, // 700
      letterSpacing: "-0.2px",
      lineHeight: 1.2,
    },
    // Muted second line under a page title.
    pageSubtitle: {
      fontFamily: typographyTokens.family.body,
      fontSize: "0.78rem", // ~12.5px
      fontWeight: typographyTokens.weight.regular,
      lineHeight: 1.4,
      color: colorsFoundation.gray[500],
    },
    // Small uppercase label that heads a content section (dummy: 10px / 600).
    sectionLabel: {
      fontFamily: typographyTokens.family.body,
      fontSize: "0.625rem", // 10px
      fontWeight: typographyTokens.weight.semibold,
      lineHeight: 1.4,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: colorsFoundation.gray[500],
    },
  },

  // Spacing
  spacing: 8, // Base unit: 8px (matches spacing.md)

  // Shape - base radius for MUI components that are not individually overridden
  shape: {
    borderRadius: 8,
  },

  // Component overrides
  components: {
    // Map custom typography variants to sensible default elements.
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          pageTitle: "h1",
          pageSubtitle: "p",
          sectionLabel: "p",
        },
      },
    },

    // Button component
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          borderRadius: borderRadius.xs,
          padding: `${dimensions.md} ${dimensions.xl}`,
          // Dummy: buttons are a consistent 13px / weight 600 across the app.
          fontSize: "0.8125rem", // 13px
          fontWeight: typographyTokens.weight.semibold,
          lineHeight: 1.5,
          textTransform: "none",
          boxSizing: "border-box",
          transition: "all 0.2s ease",
          "&.Mui-focusVisible": {
            boxShadow: focusRing,
          },
        },
        startIcon: {
          // Dummy: ~15px line icons; MUI outlined glyphs read heavier, so 16px matches.
          "& > *:nth-of-type(1)": {
            fontSize: "1rem",
          },
        },
        endIcon: {
          "& > *:nth-of-type(1)": {
            fontSize: "1rem",
          },
        },
        sizeSmall: {
          minHeight: componentHeights.buttonSm,
          padding: `${dimensions.sm} ${dimensions.md}`,
          fontSize: "0.8125rem", // 13px — matches the dummy toolbar buttons
        },
        sizeMedium: {
          minHeight: componentHeights.buttonMd,
        },
        sizeLarge: {
          minHeight: componentHeights.buttonLg,
          padding: `${dimensions.lg} ${dimensions["2xl"]}`,
        },
        contained: {
          border: `${borderWidth.xs} solid transparent`,
          "&:hover": {
            //transform: 'translateY(-1px)',
            boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
          },
          "&:active": {
            //transform: 'translateY(0)'
          },
        },
        outlined: {
          borderWidth: borderWidth.xs,
          "&:hover": {
            borderWidth: borderWidth.xs,
            backgroundColor: colorsSemantic.surface.accent.gold.subtlest,
          },
        },
      },
      variants: [
        // Custom 'tertiary' variant using outlined style
        {
          props: { variant: "tertiary" },
          style: {
            boxSizing: "border-box",
            backgroundColor: colorsFoundation.neutral.white,
            color: colorsSemantic.text.accent.gold,
            // Dummy: white button with a light-tint accent border (accent-200) at
            // rest, 1.5px, escalating to the full accent border on hover.
            border: `1.5px solid ${colorsFoundation.key.gold[200]}`,
            "&:hover": {
              backgroundColor: colorsSemantic.surface.accent.gold.subtlest,
              borderColor: colorsSemantic.border.accent.gold.strong,
            },
            "&:active": {
              backgroundColor: colorsSemantic.surface.accent.gold.subtle,
            },
          },
        },
      ],
    },

    // ButtonBase (for ripple control)
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },

    // InputBase component
    MuiInputBase: {
      styleOverrides: {
        root: {
          // Use component-level input tokens so all text-like inputs share the same base style
          fontSize: components.input.fontSize,
          fontFamily: typographyTokens.family.body,
          fontWeight: components.input.fontWeight,
          lineHeight: components.input.lineHeight,
          backgroundColor: components.input.colors.background,
          "&.Mui-disabled": {
            backgroundColor: colorsFoundation.neutral[50],
            color: colorsSemantic.text.disabled,
          },
        },
        input: {
          padding: `${components.input.paddingY} ${components.input.paddingX}`,
          "&::placeholder": {
            color: components.input.colors.placeholder,
            opacity: 1,
          },
        },
      },
    },

    // FormControlLabel - align label typography with v2 input tokens
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          color: components.input.colors.text,
          fontSize: components.input.fontSize,
          fontFamily: typographyTokens.family.body,
        },
      },
    },

    // OutlinedInput - centralized v2 border and focus behaviour
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: components.input.borderRadius,
          fontSize: components.input.fontSize,
          fontWeight: components.input.fontWeight,
          lineHeight: components.input.lineHeight,
          "&.Mui-focused": {
            boxShadow: focusRing,
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: components.input.colors.border.default,
            borderWidth: borderWidth.xs,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: components.input.colors.border.hover,
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: components.input.colors.border.focus,
            borderWidth: borderWidth.sm,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: components.input.colors.border.error,
          },
          "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {},
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: components.input.colors.border.disabled,
          },
        },
      },
    },

    // FormHelperText - use v2 helper text tokens
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: typographyTokens.size.sm,
          marginTop: dimensions.xs,
          marginLeft: 0,
          color: components.input.colors.helperText.default,
          "&.Mui-error": {
            color: components.input.colors.helperText.error,
          },
        },
      },
    },

    // FormLabel - use v2 label tokens
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: components.input.label.fontSize,
          fontWeight: components.input.label.fontWeight,
          color: components.input.colors.label.default,
          marginBottom: dimensions.xs,
          // For outlined inputs, ensure the shrunken label sits on a solid
          // background so the notched outline border is not visible behind it,
          // which makes the text hard to read (especially in error state).
          "&.MuiInputLabel-outlined": {
            backgroundColor: colorsSemantic.background.default,
            paddingLeft: dimensions.xs,
            paddingRight: dimensions.xs,
          },
          // In disabled state, match the input's neutral[50] background so the
          // label chip does not sit on a stark white background.
          "&.MuiInputLabel-outlined.Mui-disabled": {
            backgroundColor: colorsFoundation.neutral[50],
          },
          "&.Mui-focused": {
            color: components.input.colors.label.focus,
          },
          "&.Mui-error": {
            color: components.input.colors.label.error,
          },
          "&.Mui-disabled": {
            color: components.input.colors.label.disabled,
          },
        },
      },
    },

    // Checkbox - v2 styling via component tokens
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: components.checkbox.colors.border.default,
          "&:hover": {
            color: components.checkbox.colors.border.hover,
          },
          "&.Mui-checked": {
            color: components.checkbox.colors.border.checked,
          },
        },
      },
    },

    // Radio - reuse checkbox tokens for visual consistency
    MuiRadio: {
      styleOverrides: {
        root: {
          color: components.checkbox.colors.border.default,
          "&:hover": {
            color: components.checkbox.colors.border.hover,
          },
          "&.Mui-checked": {
            color: components.checkbox.colors.border.checked,
          },
        },
      },
    },

    // Switch - v2 toggle styling
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: components.toggle.width,
          height: components.toggle.height,
          padding: 0,
          "& .MuiSwitch-switchBase": {
            padding: "4px",
            "&.Mui-checked": {
              transform: "translateX(20px)",
              "& .MuiSwitch-thumb": {
                // Active thumb: white per v2 design
                backgroundColor: colorsFoundation.neutral.white,
              },
              "& + .MuiSwitch-track": {
                // Active track: key.gold[400] per v2 design
                backgroundColor: colorsFoundation.key.gold[400],
                opacity: 1,
              },
            },
          },
          "& .MuiSwitch-track": {
            border: `1px solid ${colorsFoundation.neutral[200]}`,
            borderRadius: components.toggle.borderRadius,
            backgroundColor: components.toggle.colors.track.off,
            opacity: 1,
          },
          "& .MuiSwitch-thumb": {
            width: components.toggle.thumbSize,
            height: components.toggle.thumbSize,
            backgroundColor: components.toggle.colors.thumb.off,
          },
        },
      },
    },

    // Dialog component
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: borderRadius.sm,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      },
    },

    // DialogTitle
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: `${dimensions["3xl"]} ${components.dialog.paddingX} 0`,
          fontSize: typographyTokens.size["2xl"],
          fontWeight: typographyTokens.weight.semibold,
          fontFamily: typographyTokens.family.heading,
          color: colorsSemantic.text.default,
          marginBottom: dimensions["2xl"],
        },
      },
    },

    // DialogContent
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: `${dimensions.xl} ${components.dialog.paddingX}`,
          border: "none",
        },
      },
    },

    // DialogActions
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: `${dimensions.lg} ${dimensions.xl} ${dimensions.xl} ${dimensions.xl}`,
          gap: dimensions.sm,
        },
      },
    },

    // Chip component for status badges
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: borderRadius.md,
          fontSize: typographyTokens.size.sm,
          fontWeight: typographyTokens.weight.medium,
          height: "auto",
          padding: `${dimensions.xs} ${dimensions.xl}`,
          "&:has(.MuiChip-deleteIcon)": {
            paddingRight: dimensions.md,
          },
        },
        sizeSmall: {
          fontSize: typographyTokens.size.xs,
          padding: `${dimensions.xxs} ${dimensions.md}`,
          minHeight: 20,
        },
        label: {
          padding: 0,
          ".MuiChip-icon + &": {
            paddingLeft: dimensions.md,
          },
        },
        labelSmall: {
          padding: 0,
          ".MuiChip-icon + &": {
            paddingLeft: dimensions.sm,
          },
        },
        deleteIcon: {
          marginLeft: dimensions.md,
          marginRight: 0,
        },
      },
      variants: [
        // Payment import status variants using semantic colors
        {
          props: { variant: "payment-import_pending" },
          style: {
            backgroundColor: colorsSemantic.label.payment_import.pending,
            color: colorsSemantic.text.default,
          },
        },
        {
          props: { variant: "payment-import_imported" },
          style: {
            backgroundColor: colorsSemantic.label.payment_import.imported,
            color: colorsSemantic.text.default,
          },
        },
        {
          props: { variant: "payment-import_assigned" },
          style: {
            backgroundColor: colorsSemantic.label.payment_import.assigned,
            color: colorsSemantic.text.default,
          },
        },
        {
          props: { variant: "payment-import_failed" },
          style: {
            backgroundColor: colorsSemantic.label.payment_import.failed,
            color: colorsSemantic.text.default,
          },
        },

        // Claim status variants using semantic colors
        {
          props: { variant: "claim-status-gespeichert" },
          style: {
            backgroundColor: colorsSemantic.label.schaden.gespeichert,
            color: colorsSemantic.text.default,
          },
        },
        {
          props: { variant: "claim-status-entwurf" },
          style: {
            backgroundColor: colorsSemantic.label.schaden.entwurf,
            color: colorsSemantic.text.default,
          },
        },
        {
          props: { variant: "claim-status-pruefung" },
          style: {
            backgroundColor: colorsSemantic.label.schaden.pruefung,
            color: colorsSemantic.text.default,
          },
        },
        {
          props: { variant: "claim-status-korrektur" },
          style: {
            backgroundColor: colorsSemantic.label.schaden.korrektur,
            color: colorsSemantic.text.default,
          },
        },
        {
          props: { variant: "claim-status-bearbeitung" },
          style: {
            backgroundColor: colorsSemantic.label.schaden.bearbeitung,
            color: colorsSemantic.text.default,
          },
        },
        {
          props: { variant: "claim-status-abgeschlossen" },
          style: {
            backgroundColor: colorsSemantic.label.schaden.abgeschlossen,
            color: colorsSemantic.text.default,
          },
        },
        {
          props: { variant: "claim-status-abgelehnt" },
          style: {
            backgroundColor: colorsSemantic.label.schaden.abgelehnt,
            color: colorsSemantic.text.default,
          },
        },
        {
          props: { variant: "claim-status-teilreguliert" },
          style: {
            backgroundColor: colorsSemantic.label.schaden.teilreguliert,
            color: colorsSemantic.text.default,
          },
        },
      ],
    },
  },
});
